import type { CSSProperties } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
	addHours,
	areIntervalsOverlapping,
	differenceInMinutes,
	eachDayOfInterval,
	eachHourOfInterval,
	format,
	getHours,
	getMinutes,
	isSameDay,
	isToday,
	startOfDay,
} from 'date-fns'
import { enUS } from 'date-fns/locale/en-US'
import { fr } from 'date-fns/locale/fr'

import { HiddenEventsBadge } from './hidden-events-badge'
import { useSchedulerCalendar } from './provider'
import { WeekAllDayRow } from './week-all-day-row'
import { WeekHeaderRow } from './week-header-row'
import type { SchedulerCalendarEvent } from './types'

const WeekCellsHeight = 56

/** Fixed width for the hours gutter in week view (first grid column). */
const WeekViewTimeColumnWidthPx = 65

type PositionedEvent = {
	event: SchedulerCalendarEvent
	height: number
	left: number
	top: number
	width: number
	zIndex: number
}

const isMultiDayEvent = (event: SchedulerCalendarEvent) => {
	return !isSameDay(event.start, event.end)
}

export const ViewWeek = () => {
	const {
		currentDate,
		events,
		selectedEventId,
		setSelectedEventId,
		visiblePeriod,
		EventComponent,
		locale,
		weekStartHour,
		weekEndHour,
		onSelectEvent,
	} = useSchedulerCalendar()

	const [now, setNow] = useState(() => new Date())
	const [hiddenEventsCount, setHiddenEventsCount] = useState(0)
	const scrollContainerRef = useRef<HTMLDivElement | null>(null)

	useEffect(() => {
		const intervalId = window.setInterval(() => {
			setNow(new Date())
		}, 60_000)

		return () => window.clearInterval(intervalId)
	}, [])

	const days = useMemo(() => {
		if (!visiblePeriod) return []

		return eachDayOfInterval({
			end: visiblePeriod.end,
			start: visiblePeriod.start,
		})
	}, [visiblePeriod])

	const formatLocale = useMemo(() => (locale === 'en' ? enUS : fr), [locale])

	const hours = useMemo(() => {
		const dayStart = startOfDay(currentDate)
		return eachHourOfInterval({
			end: addHours(dayStart, weekEndHour - 1),
			start: addHours(dayStart, weekStartHour),
		})
	}, [currentDate, weekEndHour, weekStartHour])

	const allDayEvents = useMemo(() => {
		return events
			.filter((event) => event.allDay || isMultiDayEvent(event))
			.filter((event) => {
				return days.some(
					(day) =>
						isSameDay(day, event.start) ||
						isSameDay(day, event.end) ||
						(day > event.start && day < event.end),
				)
			})
	}, [days, events])

	const processedDayEvents = useMemo(() => {
		return days.map((day) => {
			const dayEvents = events.filter((event) => {
				if (event.allDay || isMultiDayEvent(event)) return false

				return (
					isSameDay(day, event.start) ||
					isSameDay(day, event.end) ||
					(event.start < day && event.end > day)
				)
			})

			const sortedEvents = [...dayEvents].sort((a, b) => {
				if (a.start < b.start) return -1
				if (a.start > b.start) return 1
				return (
					differenceInMinutes(b.end, b.start) -
					differenceInMinutes(a.end, a.start)
				)
			})

			const positionedEvents: PositionedEvent[] = []
			const dayStart = startOfDay(day)
			const columns: {
				end: Date
				event: SchedulerCalendarEvent
				start: Date
			}[][] = []

			for (const event of sortedEvents) {
				const adjustedStart = isSameDay(day, event.start)
					? event.start
					: dayStart
				const adjustedEnd = isSameDay(day, event.end)
					? event.end
					: addHours(dayStart, 24)

				const startHour =
					getHours(adjustedStart) + getMinutes(adjustedStart) / 60
				const endHour = getHours(adjustedEnd) + getMinutes(adjustedEnd) / 60

				const top = (startHour - weekStartHour) * WeekCellsHeight
				const height = (endHour - startHour) * WeekCellsHeight

				let columnIndex = 0
				let placed = false

				while (!placed) {
					const col = columns[columnIndex] || []
					if (col.length === 0) {
						columns[columnIndex] = col
						placed = true
					} else {
						const overlaps = col.some((c) =>
							areIntervalsOverlapping(
								{ end: adjustedEnd, start: adjustedStart },
								{ end: c.end, start: c.start },
							),
						)
						if (!overlaps) placed = true
						else columnIndex++
					}
				}

				const currentColumn = columns[columnIndex] || []
				columns[columnIndex] = currentColumn
				currentColumn.push({ end: adjustedEnd, event, start: adjustedStart })

				const width = columnIndex === 0 ? 1 : 0.9
				const left = columnIndex === 0 ? 0 : columnIndex * 0.1

				positionedEvents.push({
					event,
					height,
					left,
					top,
					width,
					zIndex: 10 + columnIndex,
				})
			}

			return positionedEvents
		})
	}, [days, events, weekStartHour])

	const isCurrentWeek = useMemo(() => days.some((day) => isToday(day)), [days])

	const nowTopPx = useMemo(() => {
		const hour = getHours(now) + getMinutes(now) / 60
		return (hour - weekStartHour) * WeekCellsHeight
	}, [now, weekStartHour])

	const isNowInRange =
		nowTopPx >= 0 && nowTopPx <= (weekEndHour - weekStartHour) * WeekCellsHeight

	const weekGridTemplateColumns = `${WeekViewTimeColumnWidthPx}px repeat(${days.length}, minmax(0, 1fr))`

	useEffect(() => {
		const container = scrollContainerRef.current
		if (!container) return

		const handleScroll = () => {
			const viewportBottom = container.scrollTop + container.clientHeight

			const totalHidden = processedDayEvents.reduce((sum, eventsForDay) => {
				const hiddenForDay = eventsForDay.filter(
					(positionedEvent) => positionedEvent.top >= viewportBottom,
				).length

				return sum + hiddenForDay
			}, 0)

			setHiddenEventsCount(totalHidden)
		}

		handleScroll()

		container.addEventListener('scroll', handleScroll)

		return () => {
			container.removeEventListener('scroll', handleScroll)
		}
	}, [processedDayEvents])

	return (
		<div
			className='flex flex-1 flex-col overflow-hidden'
			style={
				{
					'--week-cells-height': `${WeekCellsHeight}px`,
				} as CSSProperties
			}
		>
			<WeekHeaderRow
				days={days}
				formatLocale={formatLocale}
				gridTemplateColumns={weekGridTemplateColumns}
			/>

			<WeekAllDayRow
				allDayEvents={allDayEvents}
				days={days}
				gridTemplateColumns={weekGridTemplateColumns}
				EventComponent={EventComponent}
				selectedEventId={selectedEventId}
				setSelectedEventId={setSelectedEventId}
				onSelectEvent={onSelectEvent}
			/>

			<div
				className='grid flex-1 overflow-y-auto'
				ref={scrollContainerRef}
				style={{ gridTemplateColumns: weekGridTemplateColumns }}
			>
				<div className='border-border/70 grid shrink-0 auto-cols-fr border-r'>
					{hours.map((hour, index) => (
						<div
							className='border-border/70 relative min-h-(--week-cells-height) border-b last:border-b-0'
							key={hour.toString()}
						>
							{index > 0 && (
								<span className='bg-card text-muted-foreground/70 absolute -top-3 left-0 flex h-6 w-full items-center justify-end pe-2 text-[10px] sm:pe-4 sm:text-xs'>
									{format(hour, 'HH:mm')}
								</span>
							)}
						</div>
					))}
				</div>

				{days.map((day, dayIndex) => (
					<div
						className='border-border/70 relative grid auto-cols-fr border-r last:border-r-0'
						data-today={isToday(day) || undefined}
						key={day.toString()}
					>
						{isCurrentWeek && isToday(day) && isNowInRange && (
							<div
								aria-hidden='true'
								className='pointer-events-none absolute right-0 left-0 z-5'
								style={{ top: `${nowTopPx}px` }}
							>
								<div className='bg-destructive/70 h-px' />
							</div>
						)}

						{(processedDayEvents[dayIndex] ?? []).map((positionedEvent) => {
							const selected = selectedEventId === positionedEvent.event.id

							return (
								<div
									className='absolute z-10 px-0.5'
									data-scheduler-event='true'
									key={positionedEvent.event.id}
									style={{
										height: `${positionedEvent.height}px`,
										left: `${positionedEvent.left * 100}%`,
										top: `${positionedEvent.top}px`,
										width: `${positionedEvent.width * 100}%`,
										zIndex: positionedEvent.zIndex,
									}}
								>
									<EventComponent
										event={positionedEvent.event}
										onSelect={(selectedEvent) => {
											setSelectedEventId(selectedEvent.id)
											onSelectEvent?.(selectedEvent)
										}}
										selected={selected}
										variant='timed'
										view='week'
									/>
								</div>
							)
						})}

						{hours.map((hour) => (
							<div
								className='border-border/70 relative min-h-(--week-cells-height) border-b last:border-b-0'
								key={hour.toString()}
							/>
						))}
					</div>
				))}
			</div>

			<HiddenEventsBadge
				count={hiddenEventsCount}
				onClick={() => {
					const container = scrollContainerRef.current
					if (!container) return

					container.scrollTo({
						top: container.scrollHeight,
						behavior: 'smooth',
					})
				}}
			/>
		</div>
	)
}
